import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export default class Animations {
  constructor() {
    this.animationToggleButtons = document.querySelectorAll(
      '.js-toggle-animations',
    )

    this.prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    this.animationsEnabled =
      this.getCookie('animationsEnabled') !== 'false' &&
      !this.prefersReducedMotion

    if (this.animationToggleButtons.length) {
      this.init()
    }
  }

  init() {
    this.updateButtonLabels()

    this.animationToggleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (this.animationsEnabled) {
          this.disableAnimations()
        } else {
          this.enableAnimations()
        }
      })
    })

    this.loadContent()

    if (!this.prefersReducedMotion) {
      this.setupAnimations()
    } else {
      this.disableAnimations()
    }
  }

  loadContent() {
    const image = document.querySelectorAll('.js-circle-without-anim')
    const imageAnim = document.querySelectorAll('.js-circle-with-anim')

    if (this.animationsEnabled) {
      image.forEach((element) => {
        element.style.display = 'none'
      })

      imageAnim.forEach((element) => {
        element.style.display = 'block'
      })
    } else {
      imageAnim.forEach((element) => {
        element.style.display = 'none'
      })

      image.forEach((element) => {
        element.style.display = 'block'
      })
    }
  }

  setupAnimations() {
    if (this.animationsEnabled) {
      // HOME BANNER

      const headerElements = document.querySelectorAll('.breadcrumb, .header')
      if (headerElements) {
        gsap.from(headerElements, {
          y: 0,
          opacity: 0,
          delay: 0,
          duration: 1,
          ease: 'power2.out',
        })
      }

      const homeElement = document.querySelector('.js-anime-home')
      if (homeElement) {
        gsap.from(homeElement, {
          y: 400,
          opacity: 0,
          duration: 1,
          ease: 'power2.out',
        })
      }

      const homeContentElement = document.querySelector(
        '.js-anime-home-banner-content',
      )
      if (homeContentElement) {
        gsap.from(homeContentElement, {
          x: -300,
          opacity: 0,
          duration: 1,
          delay: 1,
          ease: 'power2.out',
        })
      }

      const homeCloud1Element = document.querySelector(
        '.js-anime-home-banner-cloud-1',
      )
      if (homeCloud1Element) {
        gsap.from(homeCloud1Element, {
          y: -300,
          opacity: 0,
          duration: 1,
          delay: 1.5,
          ease: 'power2.out',
        })
      }

      const homeCloud2Element = document.querySelector(
        '.js-anime-home-banner-cloud-2',
      )
      if (homeCloud2Element) {
        gsap.from(homeCloud2Element, {
          y: -300,
          opacity: 0,
          duration: 1,
          delay: 1.7,
          ease: 'power2.out',
        })
      }

      const homeCloud3Element = document.querySelector(
        '.js-anime-home-banner-cloud-3',
      )
      if (homeCloud3Element) {
        gsap.from(homeCloud3Element, {
          y: -300,
          opacity: 0,
          duration: 1,
          delay: 1.9,
          ease: 'power2.out',
        })
      }

      const homeImageElement = document.querySelector(
        '.js-anime-home-banner-image',
      )
      if (homeImageElement) {
        gsap.from(homeImageElement, {
          y: 300,
          opacity: 0,
          duration: 1,
          delay: 2.2,
          ease: 'power2.out',
        })
      }

      const elements = document.querySelectorAll('.js-anime-home-wave')

      if (elements.length > 0) {
        elements.forEach((element) => {
          gsap.from(element, {
            scrollTrigger: {
              trigger: element,
              start: 'top 97%',
              end: 'top 100%',
              scrub: true,
            },
            y: 100,
            opacity: 1,
            duration: 1,
          })
        })
      }

      // SCROLL ANIME
      const leftScrollElements = document.querySelectorAll(
        '.js-anim-scroll-left',
      )
      if (leftScrollElements.length > 0) {
        leftScrollElements.forEach((element) => {
          gsap.from(element, {
            scrollTrigger: {
              trigger: element,
              start: 'top 90%',
              end: 'top 100%',
            },
            x: -200,
            opacity: 0,
            duration: 1,
            ease: 'power2.out',
          })
        })
      }

      const mainContentItems = document.querySelectorAll(
        '.main-content .item, .js-anim-scroll-top, .filter-bar, .pagination',
      )
      if (mainContentItems.length > 0) {
        mainContentItems.forEach((element) => {
          gsap.from(element, {
            scrollTrigger: {
              trigger: element,
              start: 'top 110%',
              end: 'top 100%',
            },
            y: 200,
            opacity: 0,
            duration: 1,
            ease: 'power2.out',
          })
        })
      }
    }
  }

  disableAnimations() {
    if (this.animationsEnabled) {
      this.animationsEnabled = false
      gsap.globalTimeline.clear()
      ScrollTrigger.getAll().forEach((trigger) => trigger.disable())

      document
        .querySelectorAll(
          '.js-anim, .item, .header, .breadcrumb, .filter-bar, .pagination',
        )
        .forEach((element) => {
          gsap.set(element, { clearProps: 'all' })
        })

      this.setCookie('animationsEnabled', 'false', 30)
      this.updateButtonLabels()
      this.loadContent()
    }
  }

  enableAnimations() {
    if (!this.animationsEnabled && !this.prefersReducedMotion) {
      this.animationsEnabled = true
      this.setupAnimations() // Redémarre les animations
      this.setCookie('animationsEnabled', 'true', 30)
      this.updateButtonLabels()
      this.loadContent()
    }
  }

  updateButtonLabels() {
    this.animationToggleButtons.forEach((button) => {
      const label = button.querySelector('span')
      if (label) {
        label.innerHTML = this.animationsEnabled
          ? `${
              window.ICL_LANGUAGE_CODE === 'en'
                ? 'Animations enabled <span class="visuallyhidden">Click here to disable animations</span>'
                : 'Animations activées <span class="visuallyhidden">Cliquez ici pour désactiver les animations</span>'
            }`
          : `${
              window.ICL_LANGUAGE_CODE === 'en'
                ? 'Animations disabled <span class="visuallyhidden">Click here to enable animations</span>'
                : 'Animations désactivées <span class="visuallyhidden">Cliquez ici pour activer les animations</span>'
            }`

        button.classList.toggle('anim-toggle--enable', this.animationsEnabled)
        button.classList.toggle('anim-toggle--disable', !this.animationsEnabled)
      }
    })
  }

  setCookie(name, value, days) {
    let expires = ''
    if (days) {
      const date = new Date()
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
      expires = '; expires=' + date.toUTCString()
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/'
  }

  getCookie(name) {
    const nameEQ = name + '='
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) === ' ') c = c.substring(1, c.length)
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
    }
    return null
  }
}
